class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    // Treat the compact checkout as blocked until every current cart item is verified.
    this.syncPreviewCheckoutState(true);
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      const rootUrl = window.Shopify?.routes?.root || '/';
      const previewChecks = await Promise.all(
        (cart.items || []).map(async (item) => {
          const productResponse = await fetch(`${rootUrl}products/${encodeURIComponent(item.handle)}.js`);
          if (!productResponse.ok) throw new Error('Unable to verify cart product safety');
          const product = await productResponse.json();
          const tags = Array.isArray(product.tags)
            ? product.tags
            : String(product.tags || '')
                .split(',')
                .map((tag) => tag.trim());
          return tags.includes('ayraport-preview');
        })
      );

      const hasPreview = previewChecks.some(Boolean);
      this.syncPreviewCheckoutState(hasPreview);
      if (hasPreview || !CartViewEvent) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // Fail closed in the compact notification. The full cart remains available.
      this.syncPreviewCheckoutState(true);
    }
  }

  syncPreviewCheckoutState(hasPreview) {
    this.dataset.hasPreview = String(hasPreview);
    const links = this.querySelector('.cart-notification__links');
    const checkoutForm = this.querySelector('#cart-notification-form');
    const checkoutButton = checkoutForm?.querySelector('[name="checkout"]');
    let notice = this.querySelector('[data-preview-cart-notice]');

    if (hasPreview) {
      if (checkoutButton) checkoutButton.disabled = true;
      if (checkoutForm) checkoutForm.hidden = true;
      if (!notice && links) {
        notice = document.createElement('p');
        notice.className = 'ayra-cart-preview-blocked';
        notice.dataset.previewCartNotice = '';
        notice.textContent = this.dataset.previewBlocked || '';
        links.insertBefore(notice, links.lastElementChild);
      }
      return;
    }

    if (checkoutButton) checkoutButton.disabled = false;
    if (checkoutForm) checkoutForm.hidden = false;
    if (notice) notice.remove();
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
